import { DependencyList, useLayoutEffect, useRef } from 'react';
import { useOutletContext } from 'react-router';

import { PageConfig, PageLayoutOutletContext } from '@/components/layout/PageLayout';

/**
 * Configures the nearest PageLayout ancestor (header + main) with the given options.
 * Must be called at the top of any page component rendered inside a <PageLayout> route.
 *
 * @param config - Header and main configuration. **Memoize with `useMemo`** when the object is
 *                 constructed inline — otherwise a new reference is created on every render and,
 *                 if you ever add `config` to `deps`, you will get an infinite loop.
 * @param deps   - Re-apply config when these values change (same semantics as useEffect deps).
 *                 **Required.** Pass `[]` explicitly when the config is static.
 *
 * @example — static config
 * usePageLayout({ headerProps: { showLogo: true, showProfileDropdown: true }, mainProps: { className: 'my-page' } }, []);
 *
 * @example — dynamic header content (memoize to keep the reference stable)
 * const config = useMemo(
 *   () => ({ headerProps: { showProfileDropdown: true, children: <BackButton onClick={handleBack} /> } }),
 *   [handleBack],
 * );
 */
const usePageLayout = (config: PageConfig, deps: DependencyList) => {
	const outletContext = useOutletContext<PageLayoutOutletContext | undefined>();
	const fallbackHeaderRef = useRef<HTMLElement | null>(null);
	const setPageLayout = outletContext?.setPageLayout;

	// useLayoutEffect fires before browser paint — no visible flash on first render.
	useLayoutEffect(() => {
		setPageLayout?.(config);
	}, [setPageLayout, ...deps]);

	return {
		headerRef: outletContext?.headerRef ?? fallbackHeaderRef,
		hasPageLayout: Boolean(outletContext),
	};
};

export default usePageLayout;
